/**
 * Countdown — looping MIN:SEC flash-sale timer (Savor-native).
 * Loops over data-loop-minutes forever. Anchored to the real clock
 * (remaining = loop - (now % loop)), so a page refresh never resets it and
 * every visitor sees the same remaining time.
 * Digits "roll" on change; entrance is a staggered IntersectionObserver reveal.
 * Reduced motion: the timer still ticks (it's content); decorative motion is CSS-disabled.
 */

import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

const SECOND = 1000;
const MINUTE = SECOND * 60;

class CountdownSectionComponent extends Component {
  #interval = null;
  #io = null;
  #reducedMotion = false;

  connectedCallback() {
    super.connectedCallback();
    this.#reducedMotion = prefersReducedMotion();

    const reveals = this.querySelectorAll('.reveal');
    if (this.#reducedMotion) {
      reveals.forEach((el) => el.classList.add('is-visible'));
    } else {
      this.#io = new IntersectionObserver((entries) => {
        for (const e of entries) {
          if (e.isIntersecting) { e.target.classList.add('is-visible'); this.#io?.unobserve(e.target); }
        }
      }, { rootMargin: '0px 0px -10% 0px' });
      reveals.forEach((el) => this.#io.observe(el));
    }

    const loopMs = (Number(this.dataset.loopMinutes) || 30) * MINUTE;

    const tick = () => {
      const remaining = loopMs - (Date.now() % loopMs);
      const mins = Math.floor(remaining / MINUTE);
      const secs = Math.floor((remaining % MINUTE) / SECOND);
      this.#set('mins', String(mins).padStart(2, '0'));
      this.#set('secs', String(secs).padStart(2, '0'));
    };

    tick();
    this.#interval = setInterval(tick, SECOND);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#interval) clearInterval(this.#interval);
    this.#io?.disconnect();
  }

  #set(unit, value) {
    const el = this.querySelector(`[data-unit="${unit}"]`);
    if (!el || el.textContent === value) return;
    el.textContent = value;
    if (this.#reducedMotion) return;
    el.classList.remove('roll');
    void el.offsetWidth;
    el.classList.add('roll');
  }
}

if (!customElements.get('countdown-section-component')) {
  customElements.define('countdown-section-component', CountdownSectionComponent);
}
